import { View, StyleSheet, ScrollView } from 'react-native';
import { Button, RadioButtonGroup, Text, theme } from '@socrait/react';

import { Select } from '~/components/Select';
import { type IncludeKey, type MessageFormat, type Timeframe } from '~/services/studentData';
import { type IncludeOptions } from '~/store/studentData/studentDataStore';

import { IncludeCheckboxes } from './IncludeCheckboxes';
import { TIMEFRAME_OPTIONS } from './sharedFilters';
import { filterPanelStyles } from './sharedStyles';

const FORMAT_OPTIONS = [
  { label: 'Email message', value: 'email' as MessageFormat },
  { label: 'Phone script', value: 'phone' as MessageFormat },
  { label: 'Summary', value: 'summary' as MessageFormat },
];

type GenerateMessagePanelProps = {
  includeOptions: IncludeOptions;
  timeframe: Timeframe;
  format: MessageFormat;
  isDirty: boolean;
  hasAnyInclude: boolean;
  onIncludeChange: (key: IncludeKey, checked: boolean) => void;
  onTimeframeChange: (t: Timeframe) => void;
  onFormatChange: (f: MessageFormat) => void;
  onGenerate: () => void;
};

export function GenerateMessagePanel({
  includeOptions,
  timeframe,
  format,
  isDirty,
  hasAnyInclude,
  onIncludeChange,
  onTimeframeChange,
  onFormatChange,
  onGenerate,
}: GenerateMessagePanelProps) {
  const buttonName = isDirty ? 'Go!' : 'Make a different selection';
  const buttonVariant = isDirty && hasAnyInclude ? 'primary' : undefined;
  const buttonDisabled = !isDirty || !hasAnyInclude;

  const formatDefaultIndex = FORMAT_OPTIONS.findIndex((o) => o.value === format);

  return (
    <ScrollView style={filterPanelStyles.scrollView}>
      <View style={filterPanelStyles.container}>
        <View style={filterPanelStyles.section}>
          <Text style={filterPanelStyles.sectionLabel}>Generate a message about:</Text>
          <IncludeCheckboxes include={includeOptions} onChange={onIncludeChange} />
        </View>

        <View style={filterPanelStyles.section}>
          <Text style={filterPanelStyles.sectionLabel}>Generate a message about:</Text>
          <View style={filterPanelStyles.sectionContent}>
            <View style={styles.selectRow}>
              <View style={styles.selectContainer}>
                <Select
                  value={timeframe}
                  onChange={(value) => onTimeframeChange(value as Timeframe)}
                  options={TIMEFRAME_OPTIONS}
                />
              </View>
            </View>
          </View>
        </View>
        <View style={filterPanelStyles.section}>
          <Text style={filterPanelStyles.sectionLabel}>Message format:</Text>
          <RadioButtonGroup
            key={formatDefaultIndex}
            options={FORMAT_OPTIONS}
            defaultIndex={formatDefaultIndex}
            onSelectOption={(value) => onFormatChange(value as MessageFormat)}
            containerStyle={filterPanelStyles.sectionContent}
            labelStyle={filterPanelStyles.optionLabel}
          />
        </View>
        <Button
          name={buttonName}
          variant={buttonVariant}
          disabled={buttonDisabled}
          onPress={onGenerate}
          containerStyle={styles.generateButton}
          size="large"
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  selectRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.small,
    overflow: 'visible',
  },
  generateButton: {
    marginTop: theme.spacing.xlarge,
    width: '100%',
  },
  selectContainer: {
    width: '90%',
  },
});
